import React, { useEffect, useState } from "react";
import { Plugins } from "@capacitor/core";
import Button from "common/components/CustomButtons/Button";
import LoadingBox from "common/components/LoadingBox";
import config from "config.js";
import mcsnetLogo from "assets/img/mcsnetLogo.png";
import mcsnetTriangleIcon from "assets/img/mcsnet-triangle-icon.png";
import googleLogo from "assets/img/google-g-logo.svg";
import appleLogo from "assets/img/apple-logo-black.svg";
import microsoftLogo from "assets/img/microsoft-logo.svg";
import "./OAuthLogin.css";
import {
  restoreApiAccessFromFirebase,
  signInWithAppleForApiAccess,
  signInWithGoogleForApiAccess,
  signInWithMicrosoftForApiAccess,
} from "../firebaseApiAuth";
import { signOutFromFirebase } from "../firebaseAuth";
import { AUTH_MODE_STORAGE_KEY, enableFirebaseAuthMode } from "../authMode";
import { isNativeIosPlatform } from "../deviceInfo";

const { Browser } = Plugins;

const SESSION_RESTORE_TIMEOUT_MS = 20000;

const containerStyle = {
  minHeight: "100vh",
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr)",
  alignItems: "center",
  padding: "32px 18px",
  background:
    "radial-gradient(circle at 18% 22%, rgba(31, 180, 233, 0.16), transparent 28%), radial-gradient(circle at 82% 78%, rgba(23, 57, 99, 0.12), transparent 30%), linear-gradient(135deg, #f6f8fb 0%, #e8eff7 100%)",
  backgroundPosition: "center",
  backgroundSize: "cover",
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
};

const shellStyle = {
  width: "100%",
  maxWidth: "1060px",
  margin: "0 auto",
  display: "grid",
  gridTemplateColumns: "minmax(280px, 0.95fr) minmax(320px, 440px)",
  alignItems: "center",
  gap: "56px",
};

const introStyle = {
  color: "#173963",
};

const logoStyle = {
  width: "214px",
  maxWidth: "72vw",
  display: "block",
  marginBottom: "42px",
};

const eyebrowStyle = {
  margin: "0 0 14px",
  color: "#1fb4e9",
  fontSize: "13px",
  fontWeight: 700,
  letterSpacing: "0",
  textTransform: "uppercase",
};

const heroHeadingStyle = {
  margin: 0,
  maxWidth: "560px",
  color: "#173963",
  fontSize: "44px",
  lineHeight: 1.08,
  fontWeight: 700,
};

const heroTextStyle = {
  maxWidth: "500px",
  margin: "20px 0 0",
  color: "#52677f",
  fontSize: "17px",
  lineHeight: 1.6,
};

const panelStyle = {
  width: "100%",
  padding: "34px",
  background: "#ffffff",
  border: "1px solid rgba(23, 57, 99, 0.09)",
  borderRadius: "8px",
  boxShadow: "0 22px 48px rgba(23, 57, 99, 0.16)",
};

const headingStyle = {
  marginTop: 0,
  marginBottom: "10px",
  color: "#173963",
  fontSize: "26px",
  lineHeight: 1.2,
  fontWeight: 700,
};

const textStyle = {
  margin: "0 0 26px",
  color: "#607187",
  fontSize: "15px",
  lineHeight: 1.55,
};

const errorStyle = {
  margin: "0 0 18px",
  padding: "12px 14px",
  background: "#fff4f2",
  border: "1px solid #f4c7c2",
  borderRadius: "6px",
  color: "#8a2f25",
  fontSize: "13px",
  lineHeight: 1.45,
};

const buttonWrapStyle = {
  display: "grid",
  gap: "12px",
};

const providerButtonStyle = {
  width: "100%",
  minHeight: "48px",
  justifyContent: "center",
  borderRadius: "6px",
  fontSize: "15px",
  textTransform: "none",
  boxShadow: "none",
};

const googleButtonStyle = {
  ...providerButtonStyle,
  background: "#ffffff",
  color: "#26364a",
  border: "1px solid #d9e1ea",
};

const appleButtonStyle = {
  ...providerButtonStyle,
  background: "#ffffff",
  color: "#17202a",
  border: "1px solid #d9e1ea",
};

const microsoftButtonStyle = {
  ...providerButtonStyle,
  background: "#ffffff",
  color: "#17202a",
  border: "1px solid #d9e1ea",
};

const mcsnetButtonStyle = {
  ...providerButtonStyle,
  background: "#ffffff",
  color: "#173963",
  border: "1px solid #c8d9e8",
};

const providerIconStyle = {
  width: "22px",
  height: "22px",
  marginRight: "10px",
  display: "inline-block",
  objectFit: "contain",
};

const dividerStyle = {
  display: "grid",
  gridTemplateColumns: "1fr auto 1fr",
  alignItems: "center",
  gap: "12px",
  margin: "24px 0 10px",
  color: "#91a0b2",
  fontSize: "12px",
};

const dividerLineStyle = {
  height: "1px",
  background: "#e3e9f0",
};

const legacyButtonStyle = {
  width: "100%",
  justifyContent: "center",
  color: "#1fb4e9",
  textTransform: "none",
};

const footnoteStyle = {
  margin: "18px 0 0",
  color: "#7a8797",
  fontSize: "12px",
  lineHeight: 1.45,
  textAlign: "center",
};

const OAuthLogin = ({
  onAuthenticated,
  title,
  message,
  restoreOnMount = true,
  legacyReturnPath,
}) => {
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const returnPath =
    legacyReturnPath || new URLSearchParams(window.location.search).get("rp");

  useEffect(() => {
    if (!restoreOnMount) {
      setLoading(false);
      return undefined;
    }

    if (!isNativeIosPlatform()) {
      const restoreAccess = async () => {
        try {
          const hasAccess = await restoreApiAccessFromFirebase();
          if (hasAccess) {
            enableFirebaseAuthMode();
            if (onAuthenticated) {
              onAuthenticated();
            }
            return;
          }
        } catch (e) {
          setErrorMessage(e.message || "Unable to restore your sign-in.");
          signOutFromFirebase().catch(() => null);
        }

        setLoading(false);
      };

      restoreAccess();
      return undefined;
    }

    const restoreController = new AbortController();
    let isMounted = true;
    let restoreTimedOut = false;
    let timeoutId;
    const restoreAccess = async () => {
      try {
        const hasAccess = await restoreApiAccessFromFirebase({
          signal: restoreController.signal,
        });
        if (!isMounted || restoreTimedOut) {
          return;
        }
        if (hasAccess) {
          enableFirebaseAuthMode();
          if (onAuthenticated) {
            onAuthenticated();
          }
          return;
        }
      } catch (e) {
        if (!isMounted || restoreTimedOut) {
          return;
        }
        setErrorMessage(e.message || "Unable to restore your sign-in.");
        signOutFromFirebase().catch(() => null);
      } finally {
        window.clearTimeout(timeoutId);
        if (isMounted && !restoreTimedOut) {
          setLoading(false);
        }
      }
    };

    timeoutId = window.setTimeout(() => {
      restoreTimedOut = true;
      restoreController.abort();
      if (isMounted) {
        setErrorMessage(
          "We couldn't restore your sign-in. Please choose a sign-in method."
        );
        setLoading(false);
      }
    }, SESSION_RESTORE_TIMEOUT_MS);

    restoreAccess();

    return () => {
      isMounted = false;
      restoreController.abort();
      window.clearTimeout(timeoutId);
    };
  }, [onAuthenticated, restoreOnMount]);

  const loginWithGoogle = async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const hasAccess = await signInWithGoogleForApiAccess();
      if (hasAccess) {
        enableFirebaseAuthMode();
        if (onAuthenticated) {
          onAuthenticated();
        }
        return;
      }
      setErrorMessage("Firebase login did not return an access token.");
      setLoading(false);
    } catch (e) {
      setErrorMessage(e.message || "Unable to sign in with Google.");
      signOutFromFirebase().catch(() => null);
      setLoading(false);
    }
  };

  const loginWithApple = async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const hasAccess = await signInWithAppleForApiAccess();
      if (hasAccess) {
        enableFirebaseAuthMode();
        if (onAuthenticated) {
          onAuthenticated();
        }
        return;
      }
      setErrorMessage("Firebase login did not return an access token.");
      setLoading(false);
    } catch (e) {
      setErrorMessage(e.message || "Unable to sign in with Apple.");
      signOutFromFirebase().catch(() => null);
      setLoading(false);
    }
  };

  const loginWithMicrosoft = async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const hasAccess = await signInWithMicrosoftForApiAccess();
      if (hasAccess) {
        enableFirebaseAuthMode();
        if (onAuthenticated) {
          onAuthenticated();
        }
        return;
      }
      setErrorMessage("Firebase login did not return an access token.");
      setLoading(false);
    } catch (e) {
      const errorText = e.message || "";
      const lowerErrorText = errorText.toLowerCase();
      if (
        lowerErrorText.includes("invalid client secret") ||
        lowerErrorText.includes("invalid-credential") ||
        lowerErrorText.includes("aadsts7000215") ||
        lowerErrorText.includes("code_exchange") ||
        lowerErrorText.includes("malformed response")
      ) {
        setErrorMessage(
          "Microsoft sign-in is not configured correctly yet. Please continue with Google, Apple, or @mcsnet.ca while we fix it."
        );
      } else if (lowerErrorText.includes("verify your email")) {
        setErrorMessage(
          "Microsoft signed you in, but it did not provide a verified email that MyAccount can use yet. Please verify the email on your Microsoft account, or continue with Google, Apple, or @mcsnet.ca."
        );
      } else {
        setErrorMessage(errorText || "Unable to sign in with Microsoft.");
      }
      signOutFromFirebase().catch(() => null);
      setLoading(false);
    }
  };

  const loginWithLegacyAuth = async () => {
    window.localStorage.removeItem(AUTH_MODE_STORAGE_KEY);
    const authQuery = ["r=" + encodeURIComponent(config.AUTH.REDIRECT_PARAM)];
    if (isNativeIosPlatform()) {
      // The browser keeps its identity session after the app logs out. Require
      // an interactive sign-in so a remembered session cannot immediately
      // redirect into the website instead of returning to the native app.
      authQuery.push("route=refreshpassword");
    }
    if (returnPath) {
      authQuery.push("rp=" + encodeURIComponent(returnPath));
    }
    const legacyAuthUrl =
      config.AUTH.ADDRESS + config.AUTH.APP_PATH + "?" + authQuery.join("&");

    if (isNativeIosPlatform()) {
      try {
        await Browser.open({ url: legacyAuthUrl });
      } catch (error) {
        setErrorMessage(
          error.message || "Unable to open the @mcsnet.ca sign-in page."
        );
      }
      return;
    }

    window.location.replace(legacyAuthUrl);
  };

  if (loading) {
    return <LoadingBox />;
  }

  return (
    <div style={containerStyle}>
      <div className="oauth-login-shell" style={shellStyle}>
        <div className="oauth-login-intro" style={introStyle}>
          <img
            className="oauth-login-logo"
            src={mcsnetLogo}
            alt="MCSnet"
            style={logoStyle}
          />
          <p style={eyebrowStyle}>Secure customer access</p>
          <h1 className="oauth-login-heading" style={heroHeadingStyle}>
            Sign in to manage your MCSnet services.
          </h1>
          <p className="oauth-login-copy" style={heroTextStyle}>
            Use a trusted provider for faster access to billing, account
            details, documents, and service setup.
          </p>
        </div>
        <div className="oauth-login-panel" style={panelStyle}>
          <h2 style={headingStyle}>{title || "Sign in to MyAccount"}</h2>
          <p style={textStyle}>
            {message || "Use your preferred sign-in provider to continue."}
          </p>
          {errorMessage ? <p style={errorStyle}>{errorMessage}</p> : null}
          <div style={buttonWrapStyle}>
            <Button style={googleButtonStyle} onClick={loginWithGoogle}>
              <img src={googleLogo} alt="" style={providerIconStyle} />
              Continue with Google
            </Button>
            <Button style={appleButtonStyle} onClick={loginWithApple}>
              <img src={appleLogo} alt="" style={providerIconStyle} />
              Continue with Apple
            </Button>
            <Button style={microsoftButtonStyle} onClick={loginWithMicrosoft}>
              <img src={microsoftLogo} alt="" style={providerIconStyle} />
              Continue with Microsoft
            </Button>
            <Button style={mcsnetButtonStyle} onClick={loginWithLegacyAuth}>
              <img src={mcsnetTriangleIcon} alt="" style={providerIconStyle} />
              Continue with @mcsnet.ca
            </Button>
            <div style={dividerStyle}>
              <span style={dividerLineStyle} />
              <span>Alternative access</span>
              <span style={dividerLineStyle} />
            </div>
            <Button
              color="primaryLightBlue"
              simple
              style={legacyButtonStyle}
              onClick={loginWithLegacyAuth}
            >
              Use email and password instead
            </Button>
          </div>
          <p style={footnoteStyle}>
            Legacy sign-in is available for accounts that have not moved to
            provider-based access yet.
          </p>
        </div>
      </div>
    </div>
  );
};

export default OAuthLogin;
